/**
 * Pack configuration, client side.
 *
 * The catalogue is generated, not stored: a pack is a tier and a card count,
 * and `@exlyr/shared` turns those two into a priced pack. The browser runs the
 * *same* generator the API does, so the card slider can price itself on every
 * step with no round trip and no chance of the two disagreeing.
 *
 * The server stays authoritative where it matters — it re-resolves the id and
 * charges from its own copy when a pack is actually opened. This is display.
 */
import {
  clampCards,
  DEFAULT_CARDS,
  parsePackId,
  priceTable,
  resolvePack,
  type PackConfig,
  type Tier,
} from '@exlyr/shared'

import type { PackSummary } from './api'

export {
  DEFAULT_CARDS,
  MAX_CARDS,
  MIN_CARDS,
  TIERS,
  clampCards,
  hitsFor,
  packId,
  priceFor,
} from '@exlyr/shared'
export type { PackConfig } from '@exlyr/shared'

/** The shape the components speak, built from a configuration. */
export function summaryFor(config: PackConfig): PackSummary {
  const pack = resolvePack(config)!
  return {
    id: pack.id,
    name: pack.name,
    tagline: pack.tagline,
    price: pack.price,
    basePrice: pack.basePrice,
    rtp: pack.rtp,
    tier: pack.tier,
    accent: pack.accent,
    cards: pack.slots.length,
    hits: pack.slots.filter((slot) => slot.label === 'Hit').length,
    art: pack.art ?? null,
    prices: priceTable(pack.tier),
  }
}

/** Falls back to the tier's default rather than 404-ing on a hand-typed URL. */
export function configFromId(id: string | undefined, fallbackTier: Tier = 'starter'): PackConfig {
  const parsed = id ? parsePackId(id) : undefined
  return parsed ?? { tier: fallbackTier, cards: DEFAULT_CARDS }
}

export function withCards(config: PackConfig, cards: number): PackConfig {
  return { ...config, cards: clampCards(cards) }
}

export function withTier(config: PackConfig, tier: Tier): PackConfig {
  return { ...config, tier }
}
